import 'src/common/styles/global.css';
import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';
import { ZHLPageHead, ZHLTheme } from '@zillow/zhl-page-structure';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { UniversalWrappers } from 'src/providers/UniversalWrappers';
import { ErrorBoundary } from 'src/components/ErrorBoundary';
import { FunctionComponentWithChildren } from 'src/utils/types';
import { isServerSide } from 'src/utils/validators/isServerSide';
import { GlobalStyle } from 'src/common/styles/GlobalStyle';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import { MaybeString } from 'src/common/types';
import { UniversalServerSideProps } from 'src/services/ServerSideProps/GetServerSidePropsBuilder';

// These errors are noisy and don't provide any value because we intentionally know better
const KNOWN_NOISY_ERROR_SNIPPETS = [
    'Invalid prop `marginBottom`',
    'Invalid prop `gap`',
    'Invalid prop `marginX`',
    'Invalid prop `alignItems` supplied to `Flex`',
    'for a non-boolean attribute',
    'Warning: useLayoutEffect ',
    'Warning: React does not recognize the `isActive` prop on a DOM element.',
];

const UNIVERSAL_ANALYTICS_SDK_VALIDATION_INDICATOR = 'Web SDK Validation';
/* eslint-disable */
const actualError = console.error;

console.error = (...data) => {
    const isUniversalAnalyticsSdkNoise = (data: string[]) =>
        data.some(
            (message: string | string[]) =>
                typeof message === 'string' && message.includes(UNIVERSAL_ANALYTICS_SDK_VALIDATION_INDICATOR)
        );

    const isKnownNoisyError = (data: string[]) =>
        data.some((message: string) => KNOWN_NOISY_ERROR_SNIPPETS.some(snippet => `${message}`.includes(snippet)));
    if (Array.isArray(data)) {
        if (isUniversalAnalyticsSdkNoise(data)) {
            console.info('Suppressed Universal Analytics SDK noise');
            return;
        }
        if (isKnownNoisyError(data)) {
            console.info('Suppressed invalid noisy prop notification');
            return;
        }
    }
    actualError(...data);
};
/* eslint-enable */

const PageCannotLoad = dynamic(async () => {
    const { WiredPageCannotLoad: Component } = await import(
        /* webpackChunkName: "PageCannotLoad" */
        'src/experiences/Error/PageCannotLoad/PageCannotLoad.wired'
    );
    return Component;
});

const MortgageApplicationCenterApp = ({ Component, pageProps }: AppProps) => {
    const {
        session,
        sessionStatus,
        experiments,
        experimentClientInitialized,
        requestedExperiments,
        loanID,
        clientSideConfig,
        trackingID,
    }: UniversalServerSideProps & {
        /**
         * Some but not all routes have the loanID
         */
        loanID?: MaybeString;
    } = pageProps;
    const WiredUniversalWrappers: FunctionComponentWithChildren = ({ children }) => (
        <UniversalWrappers
            requestURL={pageProps.requestURL}
            trackingID={trackingID}
            loanID={loanID}
            experiments={experiments ?? {}}
            experimentClientInitialized={!!experimentClientInitialized}
            requestedExperiments={requestedExperiments ?? []}
            session={session}
            sessionStatus={sessionStatus}
            clientSideConfig={clientSideConfig}>
            {children}
        </UniversalWrappers>
    );
    useEffect(() => {
        /**
         * https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm#instrument-your-application
         *
         * This needs to be initialized here at _app.tsx to ensure RUM always initiates as early as possible
         */
        DatadogRUMService.Client.initializeDatadogSDK();
    }, []);
    return (
        <>
            <ZHLPageHead currentDomain={isServerSide() ? '' : window.location.origin} />
            <ThemeProvider theme={ZHLTheme}>
                <GlobalStyle />
                <ErrorBoundary
                    onError={(error, context) => {
                        const client = DatadogRUMService.Client.getInstance();
                        client.addError(error as Error, context);
                    }}
                    fallback={
                        <WiredUniversalWrappers>
                            <PageCannotLoad />
                        </WiredUniversalWrappers>
                    }>
                    <WiredUniversalWrappers>
                        <Component {...pageProps} />
                    </WiredUniversalWrappers>
                </ErrorBoundary>
            </ThemeProvider>
        </>
    );
};

export default MortgageApplicationCenterApp;
